import { useState } from "react";
import { Download, Search } from "lucide-react";
import type { ListResponse } from "@shared-core/data/adapter";
import type {
  SupplierKit,
  SupplierKitDrop,
} from "@shared-core/data/supplier-portal-adapter";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useLoad } from "@shared-core/domain/useLoad";
import {
  KIT_WORDING,
  PAGE_WORDING,
  kitDropTone,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { StatusPill } from "../../app/components/domain/StatusPill";

/** Places a page: a kit can have 2,000 drops, so the portal reads them 50 at a time. */
export const KIT_DROPS_PAGE_SIZE = 50;

/**
 * A bought kit's line (V1 completion R5): how many packs and places, the list of where each pack goes (searched and
 * paged on the server, opened on request), and the manifest of what each pack holds as a CSV. The places are the
 * delivery details the supplier packs against; the manifest names the order's own line, never ours.
 */
export function KitDrops({
  kit,
  loadDrops,
  onDownloadManifest,
}: {
  kit: SupplierKit;
  loadDrops: (
    dropsUrl: string,
    options: { q?: string; page: number; pageSize: number },
  ) => Promise<ListResponse<SupplierKitDrop>>;
  /** Fetches and saves the manifest; rejects when it couldn't be fetched. */
  onDownloadManifest: (manifestUrl: string) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const download = async () => {
    setIsBusy(true);
    setDownloadFailed(false);
    try {
      await onDownloadManifest(kit.manifest_url);
    } catch {
      setDownloadFailed(true);
    } finally {
      setIsBusy(false);
    }
  };
  return (
    <section
      aria-label={KIT_WORDING.heading}
      className="grid gap-2 rounded-md border border-dashed border-border bg-muted/30 p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 className="text-sm font-semibold">{KIT_WORDING.heading}</h4>
        <span className="text-sm text-muted-foreground">
          {KIT_WORDING.summary(kit.kits, kit.drops)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? KIT_WORDING.hide : KIT_WORDING.show}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isBusy}
          onClick={() => void download()}
        >
          <Download aria-hidden className="size-4" />
          {KIT_WORDING.manifest}
        </Button>
        <span className="text-xs text-muted-foreground">
          {KIT_WORDING.manifestHint}
        </span>
      </div>
      {downloadFailed && (
        <p role="alert" className="text-sm text-red-600">
          {PAGE_WORDING.downloadFailed(1)}
        </p>
      )}
      {isOpen && <DropsTable kit={kit} loadDrops={loadDrops} />}
    </section>
  );
}

function DropsTable({
  kit,
  loadDrops,
}: {
  kit: SupplierKit;
  loadDrops: (
    dropsUrl: string,
    options: { q?: string; page: number; pageSize: number },
  ) => Promise<ListResponse<SupplierKitDrop>>;
}) {
  const [typed, setTyped] = useState("");
  const [page, setPage] = useState(1);
  const q = useDebouncedValue(typed.trim(), 300);
  const [searched, setSearched] = useState(q);
  // A new search starts again at page 1 (list-endpoints contract).
  if (searched !== q) {
    setSearched(q);
    setPage(1);
  }
  const { data, error, reload } = useLoad(
    () =>
      loadDrops(kit.drops_url, {
        q: q || undefined,
        page,
        pageSize: KIT_DROPS_PAGE_SIZE,
      }),
    `${kit.drops_url}|${q}|${page}`,
  );
  const first = data ? (data.page - 1) * data.pageSize + 1 : 0;
  return (
    <div className="grid gap-2">
      <div className="relative w-full max-w-xs">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground"
        />
        <Input
          type="search"
          className="pl-8"
          placeholder={KIT_WORDING.search}
          aria-label={KIT_WORDING.search}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </div>
      {error !== null && data === null && (
        <p role="alert" className="text-sm text-red-600">
          {KIT_WORDING.failed}{" "}
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            onClick={reload}
          >
            Try again
          </Button>
        </p>
      )}
      {data === null && error === null && (
        <p role="status" className="text-sm text-muted-foreground">
          {KIT_WORDING.loading}
        </p>
      )}
      {data !== null && data.items.length === 0 && (
        <p className="py-3 text-center text-sm text-muted-foreground">
          {q ? KIT_WORDING.noMatch : KIT_WORDING.noDrops}
        </p>
      )}
      {data !== null && data.items.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{KIT_WORDING.columns.drop}</TableHead>
                <TableHead>{KIT_WORDING.columns.place}</TableHead>
                <TableHead>{KIT_WORDING.columns.postcode}</TableHead>
                <TableHead className="text-right">
                  {KIT_WORDING.columns.packs}
                </TableHead>
                <TableHead>{KIT_WORDING.columns.status}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((drop) => (
                <TableRow key={drop.number}>
                  <TableCell className="tabular-nums">{drop.number}</TableCell>
                  <TableCell>
                    {drop.place}
                    {drop.address_lines.length > 0 && (
                      <span className="block text-xs text-muted-foreground">
                        {drop.address_lines
                          .filter((line) => line !== drop.postcode)
                          .join(", ")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {drop.postcode ?? ""}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {drop.kits.toLocaleString("en-GB")}
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={drop.status_label}
                      tone={kitDropTone(drop.status)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground" role="status">
              {KIT_WORDING.rows(
                first,
                first + data.items.length - 1,
                data.total,
              )}
            </p>
            {data.totalPages > 1 && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={data.page <= 1}
                  onClick={() => setPage(data.page - 1)}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={data.page >= data.totalPages}
                  onClick={() => setPage(data.page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
